import './index.css';
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { localeManager, t } from '../../services/locale';
import { themeManager } from '../../services/theme';
import type { SectionAxisPanelOptions, SectionAxis } from './types';
import { getIcon } from '../../utils/icon-manager';

/**
 * 轴向剖切面板组件
 * 第一行：隐藏（toggle）、反向
 * 第二行：X、Y、Z（互斥按钮组）
 */
export class SectionAxisPanel implements IBimComponent {
    public element: HTMLElement;
    private options: SectionAxisPanelOptions;

    private isHidden: boolean = false;
    private isFilled: boolean = false;
    private activeAxis: SectionAxis = 'x';

    private hideBtn!: HTMLButtonElement;
    private reverseBtn!: HTMLButtonElement;
    private fillBtn!: HTMLButtonElement;
    private hideLabelEl!: HTMLElement;
    private reverseLabelEl!: HTMLElement;
    private fillLabelEl!: HTMLElement;

    // DOM 引用 - 第二行
    private axisXBtn!: HTMLButtonElement;
    private axisYBtn!: HTMLButtonElement;
    private axisZBtn!: HTMLButtonElement;

    // 订阅清理
    private unsubscribeLocale: (() => void) | null = null;
    private unsubscribeTheme: (() => void) | null = null;

    constructor(options: SectionAxisPanelOptions = {}) {
        this.options = options;
        this.isHidden = options.defaultHidden ?? false;
        this.isFilled = options.defaultFill ?? false;
        this.activeAxis = options.defaultAxis ?? 'x';
        this.element = this.createDom();
    }

    /**
     * 初始化组件
     */
    public init(): void {
        // 订阅语言变更
        this.unsubscribeLocale = localeManager.subscribe(() => {
            this.setLocales();
        });

        // 订阅主题变更
        this.unsubscribeTheme = themeManager.subscribe((theme) => {
            this.setTheme(theme);
        });

        // 初始应用
        this.setLocales();
        this.setTheme(themeManager.getTheme());

        // 初始化按钮状态
        this.updateButtonStates();
        this.updateAxisButtonsState();
    }

    /**
     * 设置主题
     */
    public setTheme(theme: ThemeConfig): void {
        const style = this.element.style;
        style.setProperty('--bim-bg-inset', theme.bgInset ?? '#152232');
        style.setProperty('--bim-border-default', theme.borderDefault ?? '#334155');
        style.setProperty('--bim-border-strong', theme.borderStrong ?? '#475569');
        style.setProperty('--bim-divider', theme.divider ?? '#334155');
        style.setProperty('--bim-primary', theme.primary ?? '#3b82f6');
        style.setProperty('--bim-primary-subtle', theme.primarySubtle ?? 'rgba(59, 130, 246, 0.15)');
        style.setProperty('--bim-icon-default', theme.iconDefault ?? '#ffffff');
        style.setProperty('--bim-text-primary', theme.textPrimary ?? '#ffffff');
        style.setProperty('--bim-component-bg-hover', theme.componentBgHover ?? 'rgba(248, 250, 252, 0.06)');
    }

    /**
     * 设置语言
     */
    public setLocales(): void {
        this.hideLabelEl.textContent = t('sectionAxis.actions.hide');
        this.reverseLabelEl.textContent = t('sectionAxis.actions.reverse');
        this.fillLabelEl.textContent = t('sectionAxis.actions.fill');

        this.hideBtn.title = t('sectionAxis.actions.hide');
        this.reverseBtn.title = t('sectionAxis.actions.reverse');
        this.fillBtn.title = t('sectionAxis.actions.fill');
        this.axisXBtn.title = t('sectionAxis.actions.axisX');
        this.axisYBtn.title = t('sectionAxis.actions.axisY');
        this.axisZBtn.title = t('sectionAxis.actions.axisZ');
    }

    public setHiddenState(isHidden: boolean): void {
        this.isHidden = isHidden;
        this.updateButtonStates();
    }

    public getHiddenState(): boolean {
        return this.isHidden;
    }

    public setFillState(isFilled: boolean): void {
        this.isFilled = isFilled;
        this.updateButtonStates();
    }

    public getFillState(): boolean {
        return this.isFilled;
    }

    public setActiveAxis(axis: SectionAxis): void {
        this.activeAxis = axis;
        this.updateAxisButtonsState();
    }

    public getActiveAxis(): SectionAxis {
        return this.activeAxis;
    }

    /**
     * 销毁组件
     */
    public destroy(): void {
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }
        this.element.remove();
    }

    /**
     * 创建 DOM
     */
    private createDom(): HTMLElement {
        const root = document.createElement('div');
        root.className = 'section-axis-panel';

        // 第一行：隐藏、反向
        const row1 = document.createElement('div');
        row1.className = 'section-axis-row-1';

        this.fillBtn = this.createButton(
            'fill',
            getIcon('填充'),
            () => this.handleFillToggle()
        );

        this.hideBtn = this.createButton(
            'hide',
            getIcon('隐藏'),
            () => this.handleHideToggle()
        );

        this.reverseBtn = this.createButton(
            'reverse',
            getIcon('反向'),
            () => this.handleReverse()
        );

        row1.appendChild(this.fillBtn);
        row1.appendChild(this.hideBtn);
        row1.appendChild(this.reverseBtn);

        // 第二行：X、Y、Z
        const row2 = document.createElement('div');
        row2.className = 'section-axis-row-2';

        this.axisXBtn = this.createAxisButton('axisX', 'X', () => this.handleAxisChange('x'));
        this.axisYBtn = this.createAxisButton('axisY', 'Y', () => this.handleAxisChange('y'));
        this.axisZBtn = this.createAxisButton('axisZ', 'Z', () => this.handleAxisChange('z'));

        row2.appendChild(this.axisXBtn);
        row2.appendChild(this.axisYBtn);
        row2.appendChild(this.axisZBtn);

        root.appendChild(row1);
        root.appendChild(row2);

        return root;
    }

    /**
     * 创建按钮（带图标）
     */
    private createButton(
        type: 'hide' | 'reverse' | 'fill',
        iconSvg: string,
        onClick: () => void
    ): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'section-axis-btn';

        // 图标
        const icon = document.createElement('div');
        icon.className = 'section-axis-btn-icon';
        icon.innerHTML = iconSvg;
        btn.appendChild(icon);

        // 标签
        const label = document.createElement('div');
        label.className = 'section-axis-btn-label';
        btn.appendChild(label);

        if (type === 'hide') {
            this.hideLabelEl = label;
        } else if (type === 'reverse') {
            this.reverseLabelEl = label;
        } else if (type === 'fill') {
            this.fillLabelEl = label;
        }

        // 点击事件
        btn.addEventListener('click', onClick);

        return btn;
    }

    /**
     * 创建轴向按钮（仅文字）
     */
    private createAxisButton(
        _type: 'axisX' | 'axisY' | 'axisZ',
        text: string,
        onClick: () => void
    ): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'section-axis-btn section-axis-btn-text';

        // 文字标签（既是图标也是标签）
        const label = document.createElement('div');
        label.className = 'section-axis-btn-label';
        label.textContent = text;
        btn.appendChild(label);

        // 点击事件
        btn.addEventListener('click', onClick);

        return btn;
    }

    /**
     * 处理隐藏按钮切换
     */
    private handleHideToggle(): void {
        this.isHidden = !this.isHidden;
        this.updateButtonStates();

        if (this.options.onHideToggle) {
            this.options.onHideToggle(this.isHidden);
        }
    }

    private handleFillToggle(): void {
        this.isFilled = !this.isFilled;
        this.updateButtonStates();

        if (this.options.onFillToggle) {
            this.options.onFillToggle(this.isFilled);
        }
    }

    private handleReverse(): void {
        if (this.options.onReverse) {
            this.options.onReverse();
        }
    }

    /**
     * 处理轴向切换
     */
    private handleAxisChange(axis: SectionAxis): void {
        if (this.activeAxis === axis) {
            return; // 已经是激活状态，不重复触发
        }

        this.activeAxis = axis;
        this.updateAxisButtonsState();

        if (this.options.onAxisChange) {
            this.options.onAxisChange(axis);
        }
    }

    private updateButtonStates(): void {
        this.hideBtn.classList.toggle('active', this.isHidden);
        this.fillBtn.classList.toggle('active', this.isFilled);
    }

    /**
     * 更新轴向按钮状态
     */
    private updateAxisButtonsState(): void {
        this.axisXBtn.classList.toggle('active', this.activeAxis === 'x');
        this.axisYBtn.classList.toggle('active', this.activeAxis === 'y');
        this.axisZBtn.classList.toggle('active', this.activeAxis === 'z');
    }
}
